import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpResponse} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {ErrorHandlerService} from './error-handler.service';
import {StoreService} from '../core/common/store/store.service';
import {Configuration} from '../core/common/configuration';
import {SettingService} from '../core/common/settings.service';
import {catchError, retry} from 'rxjs/operators';


@Injectable()
export class ServicesService {

  constructor(private http: HttpClient,
              private settings: SettingService,
              private errorHandler: ErrorHandlerService,
              private storeService: StoreService,
              private config: Configuration) {
  }

  createAndSetDefaultHeaders(): HttpHeaders {
    const httpHaeders = new HttpHeaders();
    return this.setHeaders(httpHaeders);
  }

  getHeaders(): Headers[] {
    const headers: Headers[] = [];
    const httpHeaders = this.createAndSetDefaultHeaders();
    httpHeaders.keys().forEach(
      key => {
        const value = httpHeaders.get(key);
        if (value != null) {
          const header: Headers = new Headers();
          header.set(key, value);
          headers.push(header);
        }
      });
    return headers;
  }

  get<T>(typeUrl: string,
         params: any,
         errorMessage?: string,
         httpHeaders?: HttpHeaders,
         retryNumber?: number): Observable<HttpResponse<T>> {
    const headers = httpHeaders ? this.setHeaders(httpHeaders) : this.createAndSetDefaultHeaders();
    const httpResponse = this.http.get<T>(
      this.config.HTTP_RESOURCES + typeUrl, {headers, params, observe: 'response'});
    return this.retryAndCatch<T>(httpResponse, errorMessage, retryNumber);
  }

  haed<T>(typeUrl: string,
          params: any,
          errorMessage?: string,
          httpHeaders?: HttpHeaders,
          retryNumber?: number): Observable<HttpResponse<T>> {
    const headers = httpHeaders ? this.setHeaders(httpHeaders) : this.createAndSetDefaultHeaders();
    const httpResponse = this.http.head<T>(
      this.config.HTTP_RESOURCES + typeUrl, {headers, params, observe: 'response'});
    return this.retryAndCatch<T>(httpResponse, errorMessage, retryNumber);
  }

  post<T>(typeUrl: string,
          body: any,
          errorMessage?: string,
          retryNumber?: number,
          httpHeaders?: HttpHeaders): Observable<HttpResponse<T>> {
    const headers = httpHeaders ? this.setHeaders(httpHeaders) : this.createAndSetDefaultHeaders();
    const httpResponse = this.http.post<T>(
      this.config.HTTP_RESOURCES + typeUrl, body, {headers, observe: 'response'});
    return this.retryAndCatch(httpResponse, errorMessage, retryNumber);
  }

  put<T>(typeUrl: string,
         body: any,
         errorMessage?: string,
         retryNumber?: number,
         httpHeaders?: HttpHeaders): Observable<HttpResponse<T>> {
    const headers = httpHeaders ? this.setHeaders(httpHeaders) : this.createAndSetDefaultHeaders();
    const httpResponse = this.http.put<T>(
      this.config.HTTP_RESOURCES + typeUrl, body, {headers, observe: 'response'});
    return this.retryAndCatch(httpResponse, errorMessage, retryNumber);
  }

  patch<T>(typeUrl: string,
           body: any,
           errorMessage?: string,
           retryNumber?: number,
           httpHeaders?: HttpHeaders): Observable<HttpResponse<T>> {
    const headers = httpHeaders ? this.setHeaders(httpHeaders) : this.createAndSetDefaultHeaders();
    const httpResponse = this.http.patch<T>(
      this.config.HTTP_RESOURCES + typeUrl, body, {headers, observe: 'response'});
    return this.retryAndCatch(httpResponse, errorMessage, retryNumber);
  }

  delete<T>(typeUrl: string,
            params: any,
            errorMessage?: string,
            retryNumber?: number,
            httpHeaders?: HttpHeaders): Observable<HttpResponse<T>> {
    const headers = httpHeaders ? this.setHeaders(httpHeaders) : this.createAndSetDefaultHeaders();
    const httpResponse = this.http.delete<T>(
      this.config.HTTP_RESOURCES + typeUrl, {headers, params, observe: 'response'});
    return this.retryAndCatch(httpResponse, errorMessage, retryNumber);
  }

  getFiles<T>(url: string,
              params: any,
              errorMessage?: string,
              httpHeaders?: HttpHeaders,
              retryNumber?: number): Observable<HttpResponse<T>> {
    const headers = httpHeaders ? this.setHeaders(httpHeaders) : this.createAndSetDefaultHeaders();
    const httpResponse = this.http.get<T>(url, {headers, params, observe: 'response'});
    return this.retryAndCatch<T>(httpResponse, errorMessage, retryNumber);
  }

  deleteFile<T>(url: string,
                params: any,
                errorMessage?: string,
                retryNumber?: number,
                httpHeaders?: HttpHeaders): Observable<HttpResponse<T>> {
    const headers = httpHeaders ? this.setHeaders(httpHeaders) : this.createAndSetDefaultHeaders();
    const httpResponse = this.http.delete<T>(url, {headers, params, observe: 'response'});
    return this.retryAndCatch(httpResponse, errorMessage, retryNumber);
  }

  download(url: string, params: any, httpHeaders?: HttpHeaders): Observable<Blob> {
    const headers = httpHeaders ? this.setHeaders(httpHeaders) : this.createAndSetDefaultHeaders();
    return this.http.get(url, {
      headers,
      params,
      responseType: 'blob'
    });
  }

  logout<T>(typeUrl: string, errorMessage: string): Observable<T> {
    return this.http
      .get<T>(this.config.HTTP_RESOURCES + typeUrl, {headers: this.createAndSetDefaultHeaders()})
      .pipe(catchError((e: any) => throwError(this.errorHandler.dispatch(e, errorMessage))));
  }

  lock<T>(typeUrl: string, errorMessage: string): Observable<T> {
    return this.http
      .get<T>(this.config.HTTP_RESOURCES + typeUrl, {
        headers: this.createAndSetDefaultHeaders(),
        params: {lock: true}
      })
      .pipe(catchError((e: any) => throwError(this.errorHandler.dispatch(e, errorMessage))));
  }

  login<T>(typeUrl: string, params: any): Observable<HttpResponse<T>> {
    return this.http
      .get<T>(this.config.HTTP_RESOURCES + typeUrl,
        {headers: this.createAndSetDefaultHeaders(), params, observe: 'response'});
  }

  auth<T>(token: string, typeUrl: string, params: any): Observable<HttpResponse<T>> {
    return this.http.head<T>(
      this.config.HTTP_RESOURCES + typeUrl,
      {headers: this.setHeaderRecaptcha(token), params, observe: 'response'});
  }

  setHeaderRecaptcha(token: string): HttpHeaders {
    const httpHaeders = this.createAndSetDefaultHeaders();
    return httpHaeders.set(
      this.config.HEADER_FIELDS.reCaptchaToken, token);
  }

  private setHeaders(httpHaeders: HttpHeaders): HttpHeaders {
    const confirmTwoFa = this.storeService.getTwoFaRequest();
    if (this.storeService.isValidTwoFaRequest() && confirmTwoFa != null) {
      httpHaeders = httpHaeders.set(this.config.HEADER_FIELDS.twoFaRequestCode, confirmTwoFa);
    }
    return httpHaeders;
  }

  private retryAndCatch<T>(httpResponse: Observable<HttpResponse<T>>,
                           errorMessage?: string,
                           retryNumber: number = this.config.NUM_RETRY): Observable<HttpResponse<T>> {
    return httpResponse
      .pipe(
        retry(retryNumber),
        catchError((e: any) => {
          this.settings.disableLoader();
          if (errorMessage) {
            return throwError(new Error(this.errorHandler.dispatch(e, errorMessage)));
          }
          return throwError(e);
        })
      );
  }

}
